import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "@/api/client";
import { useMe } from "@/api/me";

export type LedgerOrgRow = {
  org_id: string;
  org_slug: string;
  org_name: string;
  balance_micros: number;
  balance_credits: number;
  // The two buckets behind the generated total: the monthly plan
  // allowance (forfeited at the period break) and credits the org owns
  // outright (never expire).
  subscription_balance_micros: number;
  purchased_balance_micros: number;
  subscription_balance_credits: number;
  purchased_balance_credits: number;
  used_micros: number;
  added_micros: number;
  system_used_micros: number;
  byoa_used_micros: number;
  below_cap: boolean;
  // Raw per-org overrides (null = inherit the global default). The
  // setter UI reads BOTH and re-submits BOTH on every save because the
  // /config endpoint is a full-replacement upsert.
  enforce_balance: boolean | null;
  negative_cap_micros: number | null;
  // Per-org cost/credit display visibility overrides (null = inherit global).
  cost_visibility: string | null;
  credits_visibility: string | null;
};

export type LedgerSummary = {
  micros_per_credit: number;
  credits_enabled: boolean;
  orgs: LedgerOrgRow[];
};

export type LedgerEntry = {
  /** null on a `daily` row: it is an aggregate of many entries, not one. */
  id: number | null;
  kind: string;
  amount_micros: number;
  amount_credits: number;
  balance_after_micros: number;
  // Per-bucket split of amount_micros. Rows written before the
  // two-bucket migration carry 0 deltas and null *_after.
  subscription_delta_micros: number;
  purchased_delta_micros: number;
  subscription_after_micros: number | null;
  purchased_after_micros: number | null;
  multiplier_kind: string | null;
  /** `usage_run` rows only: the stretch of turns this folds, and how
   *  many. A run never crosses a top-up, reset or day boundary. */
  first_at?: string;
  last_at?: string;
  event_count?: number;
  multiplier_bp: number | null;
  raw_cost_micros: number | null;
  note: string | null;
  created_at: string;
};

export type OrgLedger = {
  org_slug: string;
  org_name: string;
  balance_micros: number;
  balance_credits: number;
  subscription_balance_micros: number;
  purchased_balance_micros: number;
  subscription_balance_credits: number;
  purchased_balance_credits: number;
  resolved_cap_micros: number;
  below_cap: boolean;
  micros_per_credit: number;
  credits_enabled: boolean;
  /** ISO date the subscription bucket resets, or null on no plan.
   *  Included credits are forfeited at that break, so a balance shown
   *  without it overstates what the org actually has time to spend. */
  subscription_period_end: string | null;
};

export function useAdminLedgerSummary() {
  // ``/admin/ledger/summary`` is server-side scoped by active_org_id, so
  // the active org slug joins the queryKey — rapid org-switching can't
  // serve a stale cross-org rollup before the refetch lands.
  const activeOrgSlug = useMe().data?.admin_scope?.active_org_slug ?? null;
  return useQuery({
    queryKey: ["admin", "ledger", "summary", activeOrgSlug],
    queryFn: async () => (await api.get<LedgerSummary>("/admin/ledger/summary")).data,
    staleTime: 30_000,
  });
}

export function useAdminLedgerEntries(orgId: string | null) {
  return useQuery({
    queryKey: ["admin", "ledger", "entries", orgId],
    enabled: orgId !== null,
    queryFn: async () =>
      (await api.get<{ entries: LedgerEntry[] }>(`/admin/ledger/orgs/${orgId}/entries`)).data
        .entries,
  });
}

export function useTopupOrgLedger() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      orgId: string;
      amount_credits: number;
      kind: "topup" | "adjustment";
      // Which bucket the credits land in: "subscription" is the monthly
      // plan allowance (forfeited at the period break), "purchased" is
      // credit the org owns outright. Optional so a caller that predates
      // the split keeps compiling; the hook still POSTs an explicit
      // value, and "purchased" is what a top-up has always meant.
      bucket?: "subscription" | "purchased";
      note: string | null;
    }) =>
      (
        await api.post(`/admin/ledger/orgs/${payload.orgId}/topup`, {
          amount_credits: payload.amount_credits,
          kind: payload.kind,
          bucket: payload.bucket ?? "purchased",
          note: payload.note,
        })
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "ledger"] }),
  });
}

/** Superadmin per-org credit-mode setter. The backend ``/config``
 *  route is a FULL-REPLACEMENT upsert: it writes BOTH columns every
 *  call, so the caller must always submit both ``enforce_balance`` and
 *  ``negative_cap_micros`` (null = inherit the global default), or it
 *  silently nulls the field it omits. Invalidates both the ledger and
 *  the cost views since the display policy depends on the enforce flag. */
export function useSetOrgCreditMode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      orgId: string;
      enforce_balance: boolean | null;
      negative_cap_micros: number | null;
      cost_visibility: string | null;
      credits_visibility: string | null;
    }) =>
      (
        await api.post(`/admin/ledger/orgs/${payload.orgId}/config`, {
          enforce_balance: payload.enforce_balance,
          negative_cap_micros: payload.negative_cap_micros,
          cost_visibility: payload.cost_visibility,
          credits_visibility: payload.credits_visibility,
        })
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "ledger"] });
      qc.invalidateQueries({ queryKey: ["admin", "costs"] });
    },
  });
}

export function useOrgLedger(slug: string) {
  return useQuery({
    queryKey: ["org", "ledger", slug],
    // An empty slug means no organisation is selected. Without this
    // the hook requests "/orgs//ledger", a malformed path that 404s
    // on every render of the all-orgs view.
    enabled: Boolean(slug),
    queryFn: async () => (await api.get<OrgLedger>(`/orgs/${slug}/ledger`)).data,
    staleTime: 30_000,
  });
}

/** How to look at an org's credit movements.
 *
 *  Every turn writes one debit, so a working org's ledger is
 *  overwhelmingly agent usage and the few deliberate events are buried
 *  in it. */
export type LedgerView = "verbose" | "daily" | "grants";

export type LedgerPage = {
  view: LedgerView;
  entries: LedgerEntry[];
  has_more: boolean;
  next_before_id?: number | null;
  next_before_ts?: string | null;
};

/** One page at a time, accumulated by the caller.
 *
 *  Keyset, not offset: entries are written constantly, and an offset
 *  page repeats or skips rows whenever a turn settles between two
 *  requests. `has_more` comes from the server asking for one row more
 *  than the page, so a "load more" is never offered for a page that
 *  happens to be exactly full. */
export function useOrgLedgerEntries(
  slug: string,
  options: { view?: LedgerView; limit?: number } = {},
) {
  const { view = "verbose", limit = 50 } = options;
  return useInfiniteQuery({
    queryKey: ["org", "ledger", "entries", slug, view, limit],
    // An empty slug means no organisation is selected. Without this
    // the hook requests "/orgs//ledger", a malformed path that 404s
    // on every render of the all-orgs view.
    enabled: Boolean(slug),
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }: { pageParam: string | null }) => {
      const q = new URLSearchParams({ view, limit: String(limit) });
      if (pageParam) {
        // The grouped view's rows are not all table rows — a run has no
        // id — so it keysets on time. URLSearchParams encodes the "+" in
        // the ISO offset; a hand-built URL would send it as a space and
        // the server would refuse to parse it.
        q.set(view === "daily" ? "before_ts" : "before_id", pageParam);
      }
      return (
        await api.get<LedgerPage>(`/orgs/${slug}/ledger/entries?${q.toString()}`)
      ).data;
    },
    getNextPageParam: (last: LedgerPage) => {
      if (!last.has_more) return undefined;
      const next =
        last.view === "daily" ? last.next_before_ts : last.next_before_id;
      // A cursor of null with has_more true would loop on page one
      // forever; treat it as the end instead.
      return next == null ? undefined : String(next);
    },
  });
}
